import * as fileType from "file-type";
import utilities from "./utilities";

async function deriveKey(
  password: string,
  salt: Uint8Array,
): Promise<CryptoKey> {
  const encodedPassword = new TextEncoder().encode(password);
  const baseKey = await window.crypto.subtle.importKey(
    "raw",
    encodedPassword,
    { name: "PBKDF2" },
    false,
    ["deriveKey"],
  );
  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 600000,
      hash: "SHA-256",
    },
    baseKey,
    { name: "AES-GCM", length: 256 },
    true,
    ["encrypt", "decrypt"],
  );
}

export async function encryptFile(
  file: File | null | undefined,
  password: string,
  password_iv: string,
): Promise<File | null> {
  if (!file) {
    return null;
  }
  try {
    const ivLength = 12;
    const saltLength = 16;
    const iv = window.crypto.getRandomValues(new Uint8Array(ivLength)); // 12 bytes for AES-GCM
    const salt = window.crypto.getRandomValues(new Uint8Array(saltLength)); // 128-bit salt
    const key = await deriveKey(password, salt);
    const plainData = await file.arrayBuffer();
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128, // 128-bit tag length
      },
      key,
      plainData,
    );
    const cipherData = new Uint8Array(encryptedContent);
    const contentFile = `${password_iv}.${iv.toString()}.${cipherData.toString()}.${salt.toString()}`;
    return new File([contentFile], file.name, {
      lastModified: file.lastModified,
      type: file.type,
    });
  } catch (error) {
    return null;
  }
}

export async function encryptText(
  text: string,
  password: string,
): Promise<string> {
  try {
    const ivLength = 12;
    const saltLength = 16;
    const iv = window.crypto.getRandomValues(new Uint8Array(ivLength)); // 12 bytes for AES-GCM
    const salt = window.crypto.getRandomValues(new Uint8Array(saltLength)); // 128-bit salt
    const key = await deriveKey(password, salt);
    const plainData = new TextEncoder().encode(text);
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv,
        tagLength: 128, // 128-bit tag length
      },
      key,
      plainData,
    );
    const cipherData = new Uint8Array(encryptedContent);
    return `${iv.toString()}.${cipherData.toString()}.${salt.toString()}`;
  } catch (error) {
    return "";
  }
}

export async function decryptText(
  encryptedText: string,
  password: string,
): Promise<string> {
  try {
    const [ivStr, cipherDataStr, saltStr] = encryptedText.split(".");
    const iv = Uint8Array.from(ivStr.split(",").map((x) => parseInt(x, 10)));
    const cipherData = Uint8Array.from(
      cipherDataStr.split(",").map((x) => parseInt(x, 10)),
    );
    const salt = Uint8Array.from(
      saltStr.split(",").map((x) => parseInt(x, 10)),
    );
    const key = await deriveKey(password, salt);
    const decryptedContent = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      cipherData,
    );
    return new TextDecoder().decode(decryptedContent);
  } catch (error) {
    return "";
  }
}

export async function decryptFile(
  encryptedFile: File | null | undefined,
  password: string,
): Promise<File | null> {
  if (!encryptedFile) {
    return null;
  }
  try {
    const contentFile = await encryptedFile.text();
    const [, ivStr, cipherDataStr, saltStr] = contentFile.split(".");
    const iv = Uint8Array.from(ivStr.split(",").map((x) => parseInt(x, 10)));
    const cipherData = Uint8Array.from(
      cipherDataStr.split(",").map((x) => parseInt(x, 10)),
    );
    const salt = Uint8Array.from(
      saltStr.split(",").map((x) => parseInt(x, 10)),
    );
    const key = await deriveKey(password, salt);
    const decryptedContent = await window.crypto.subtle.decrypt(
      { name: "AES-GCM", iv, tagLength: 128 },
      key,
      cipherData,
    );
    const type = await fileType.fileTypeFromBuffer(decryptedContent);
    return new File([decryptedContent], encryptedFile.name, {
      lastModified: encryptedFile.lastModified,
      type: type?.mime,
    });
  } catch (error) {
    return null;
  }
}

export const toBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const decryptFileBase64 = async (file, password) => {
  const fileConvert = utilities.base64ToFile(
    file.file,
    file?.fileName,
    "text/plain",
  );

  const fileDecrypted = await decryptFile(fileConvert, password);
  if (!fileDecrypted) {
    return null;
  }
  return {
    ...file,
    file: await toBase64(fileDecrypted),
    fileType: fileDecrypted?.type,
  };
};
